import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { SuccessAlertContext } from "App";
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import TableCellSmall, { TableHeaderCellSmall } from "./TableCellSmall";
import type { CSVCountCheckOrder, CSVThresholdCheckStock, ManagedItem } from "../types";

const limit = 10;
type Props = {
  open: boolean;
  title: string;
  content: string;
  ActionButtons: JSX.Element;
  onClose: () => void;
  stockList: (CSVCountCheckOrder | CSVThresholdCheckStock)[];
  productList: ManagedItem[];
  isThreshold?: boolean;
  isFastLoading?: MutableRefObject<boolean>;
  getMoreStockList?: Function;
  isDeleting?: boolean;
  error?: string | undefined;
  errorTitle?: string;
  setInit?: Dispatch<SetStateAction<boolean>>;
  setItemCds: Dispatch<SetStateAction<string[]>>;
};

const tableHeadthreshold = (error: string) => (
  <TableHead>
    <TableRow>
      <TableHeaderCellSmall rowSpan={2}>商品コード</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>JANコード</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>大分類</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>仕入先名</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>ブランド</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>商品名</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>サイズ</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>ジョイフル仕切単価</TableHeaderCellSmall>
      <TableHeaderCellSmall rowSpan={2}>削除</TableHeaderCellSmall>
      {error && <TableHeaderCellSmall rowSpan={2}>エラー内容</TableHeaderCellSmall>}
    </TableRow>
  </TableHead>
);

const tableBodyThreshold = (s: CSVThresholdCheckStock, err?: string) => (
  <TableRow>
    <TableCellSmall>{s.item_cd}</TableCellSmall>
    <TableCellSmall>{s.jan_cd}</TableCellSmall>
    {s.symbolThresholdList.length >= 2 && (
      <>
        <TableCellSmall
          sx={{ textAlign: "right" }}
        >{`${s.symbolThresholdList[0].toLocaleString()}`}</TableCellSmall>
        <TableCellSmall
          sx={{ textAlign: "right" }}
        >{`${s.symbolThresholdList[1].toLocaleString()}`}</TableCellSmall>
      </>
    )}
  </TableRow>
);

/**
 * 在庫数量削除確認モーダルのテーブル
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function DeleteConfirmTable({
  open,
  title,
  content,
  ActionButtons,
  onClose,
  stockList,
  productList,
  isThreshold,
  isFastLoading,
  getMoreStockList,
  isDeleting,
  error,
  errorTitle,
  setInit,
  setItemCds,
}: Props) {
  const [getMoreLoadEvent, setGetMoreLoadEvent] = useState(false);
  const [adjustment, setAdjustment] = useState<number | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const contentRef = useRef<HTMLDivElement | undefined>(undefined);
  const innerRef = useRef<HTMLDivElement | undefined>(undefined);
  // const [itemCds, setItemCds] = useState<string[]>([]);

  const { showAlert } = useContext(SuccessAlertContext);

  const handleScroll = async () => {
    if (isFastLoading?.current || !contentRef.current) {
      return;
    }
    const scrollTop = contentRef.current?.scrollTop;
    const contentHeight = contentRef.current?.clientHeight;
    const scrollHeight = contentRef.current?.scrollHeight;
    if (scrollTop + contentHeight >= scrollHeight) {
      setGetMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    (async () => {
      if (getMoreStockList) await getMoreStockList();
    })();
  }, [getMoreLoadEvent]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (setInit) {
      setInit(true);
      if (error) showAlert(error, "error");
    }
  }, [open]);
  useEffect(() => {
    if (stockList.length === limit && !isDeleting) {
      setTimeout(() => {
        if (
          contentRef.current &&
          innerRef.current &&
          contentRef.current.clientHeight - innerRef.current.clientHeight >= 0
        ) {
          // 中のコンテンツが小さい場合、調整オブジェクトを挿入
          setAdjustment(contentRef.current.clientHeight - innerRef.current.clientHeight + 1);
        } else {
          setAdjustment(undefined);
        }
      }, 0);
    }
  }, [stockList, isDeleting]);

  useEffect(() => {
    setSelected(productList.map((r) => r.item_cd));
  }, [productList]);

  useEffect(() => {
    setItemCds(selected);
  }, [setItemCds]);

  const toggle = (itemCd: string) => {
    setSelected((prev) =>
      prev.includes(itemCd) ? prev.filter((c) => c !== itemCd) : [...prev, itemCd],
    );
  };

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>, productCd: string) => {
    if (event.target.checked) {
      setItemCds((prev) => {
        if (prev.push(productCd)) return prev;
        return [...prev, productCd];
      });
    } else {
      setItemCds((prev) => prev.filter((id) => id !== productCd));
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose()} fullScreen>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={handleScroll}>
        <Box ref={innerRef}>
          <DialogContentText>{error ? errorTitle : content}</DialogContentText>
          <TableContainer
            component={Paper}
            sx={{ overflowX: "initial", width: "fit-content", minWidth: "100%" }}
          >
            <Table sx={{ width: "100%", borderCollapse: "separate" }} aria-label="order table">
              {stockList.length !== 0 &&
                (isThreshold ? (
                  tableHeadthreshold(error!)
                ) : (
                  <TableHead>
                    <TableRow>
                      <TableHeaderCellSmall>商品コード</TableHeaderCellSmall>
                      <TableHeaderCellSmall>JANコード</TableHeaderCellSmall>
                      <TableHeaderCellSmall>大分類</TableHeaderCellSmall>
                      <TableHeaderCellSmall>仕入先名</TableHeaderCellSmall>
                      <TableHeaderCellSmall>ブランド</TableHeaderCellSmall>
                      <TableHeaderCellSmall>商品名</TableHeaderCellSmall>
                      <TableHeaderCellSmall>サイズ</TableHeaderCellSmall>
                      <TableHeaderCellSmall>ジョイフル仕切単価</TableHeaderCellSmall>
                      <TableHeaderCellSmall>削除</TableHeaderCellSmall>
                      {error && <TableHeaderCellSmall>エラー内容</TableHeaderCellSmall>}
                    </TableRow>
                  </TableHead>
                ))}
              <TableBody>
                {productList.map((row) => (
                  // eslint-disable-next-line no-param-reassign
                  <TableRow
                    key={row.item_cd}
                    selected={selected.includes(row.item_cd)}
                    onClick={() => {}}
                    hover
                  >
                    <TableCellSmall>{row.item_cd}</TableCellSmall>
                    <TableCellSmall>{row.jan_cd}</TableCellSmall>
                    <TableCellSmall>{row.class_name}</TableCellSmall>
                    <TableCellSmall>{row.vendor_name}</TableCellSmall>
                    <TableCellSmall>{row.brand}</TableCellSmall>
                    <TableCellSmall>{row.item_name}</TableCellSmall>
                    <TableCellSmall>{row.size}</TableCellSmall>
                    <TableCellSmall>{row.store_wholesale_price}</TableCellSmall>
                    <TableCellSmall>
                      <Checkbox
                        required
                        indeterminate={selected.length > 0 && selected.length < productList.length}
                        checked={productList.length > 0 && selected.length === productList.length}
                        disabled
                        onChange={() => {}}
                        inputProps={{ "aria-label": "DeleteSelected" }}
                        color="secondary"
                      />
                    </TableCellSmall>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {adjustment && <Box height={adjustment} />}
          {isDeleting && (
            <Box py={3} textAlign="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      {/* Button Area */}
      <DialogActions>{ActionButtons}</DialogActions>
    </Dialog>
  );
}
